import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog, AppBar, Toolbar, IconButton, Typography, Button, Box,
  Container, Paper, LinearProgress,
  Chip, Checkbox, Grid,
  useTheme, useMediaQuery, Tooltip, Stack
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import TimerIcon from '@mui/icons-material/Timer';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';

import { scaleIngredientAmount, detectTimers } from "../utils/recipeStandardization";

// Synthetic pleasant kitchen chime using Web Audio API
const playChime = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    // Cheerful double bell chime
    osc.frequency.setValueAtTime(587.33, ctx.currentTime); // D5
    osc.frequency.setValueAtTime(880, ctx.currentTime + 0.18); // A5
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.9);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.9);
  } catch (e) {
    console.warn("Could not play timer chime:", e);
  }
};

const CookingModeDialog = ({
  open,
  onClose,
  recipe,
  currentServings,
  onServingsChange,
  checkedIngredients,
  onToggleIngredient,
  checkedSteps,
  onToggleStep
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // State
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [viewMode, setViewMode] = useState("step"); // 'step' or 'overview'
  const [wakeLockActive, setWakeLockActive] = useState(false);
  const [wakeLockSupported, setWakeLockSupported] = useState(true);

  // Timer state
  const [timerSecondsLeft, setTimerSecondsLeft] = useState(null);
  const [timerInitialSeconds, setTimerInitialSeconds] = useState(0);
  const [timerIsRunning, setTimerIsRunning] = useState(false);
  const [activeTimerLabel, setActiveTimerLabel] = useState("");
  const wakeLockRef = useRef(null);

  const baseServings = Math.max(1, parseInt(recipe?.servings, 10) || 1);
  const scaleMultiplier = currentServings / baseServings;
  const steps = recipe?.steps || [];
  const ingredients = recipe?.ingredients || [];

  // -------------------------------------------------------------
  // 1. Screen Wake Lock API
  // -------------------------------------------------------------
  const requestWakeLock = useCallback(async () => {
    if ("wakeLock" in navigator) {
      try {
        const lock = await navigator.wakeLock.request("screen");
        wakeLockRef.current = lock;
        setWakeLockActive(true);
        setWakeLockSupported(true);
        lock.addEventListener("release", () => {
          setWakeLockActive(false);
        });
      } catch (err) {
        console.warn("Wake lock request failed:", err);
        setWakeLockActive(false);
      }
    } else {
      setWakeLockSupported(false);
    }
  }, []);

  const releaseWakeLock = useCallback(() => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
      setWakeLockActive(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      requestWakeLock();
      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible" && open) {
          requestWakeLock();
        }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        releaseWakeLock();
      };
    } else {
      releaseWakeLock();
    }
  }, [open, requestWakeLock, releaseWakeLock]);

  // Keyboard navigation (Left / Right arrow keys)
  useEffect(() => {
    if (!open || viewMode !== "step") return;
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight" && activeStepIndex < steps.length - 1) {
        setActiveStepIndex(prev => prev + 1);
      } else if (e.key === "ArrowLeft" && activeStepIndex > 0) {
        setActiveStepIndex(prev => prev - 1);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, viewMode, activeStepIndex, steps.length]);

  // -------------------------------------------------------------
  // 2. Timer Logic
  // -------------------------------------------------------------
  useEffect(() => {
    let interval = null;
    if (timerIsRunning && timerSecondsLeft !== null && timerSecondsLeft > 0) {
      interval = setInterval(() => {
        setTimerSecondsLeft(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setTimerIsRunning(false);
            playChime();
            if (navigator.vibrate) {
              navigator.vibrate([300, 100, 300, 100, 300]);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timerIsRunning, timerSecondsLeft]);

  const startTimer = (seconds, label = "Timer") => {
    setTimerInitialSeconds(seconds);
    setTimerSecondsLeft(seconds);
    setActiveTimerLabel(label);
    setTimerIsRunning(true);
  };

  const toggleTimerPause = () => {
    setTimerIsRunning(prev => !prev);
  };

  const resetTimer = () => {
    setTimerIsRunning(false);
    setTimerSecondsLeft(timerInitialSeconds);
  };

  const cancelTimer = () => {
    setTimerIsRunning(false);
    setTimerSecondsLeft(null);
    setActiveTimerLabel("");
  };

  const formatTimerDisplay = (totalSecs) => {
    if (totalSecs === null || totalSecs === undefined) return "00:00";
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    const hrs = Math.floor(mins / 60);
    if (hrs > 0) {
      const remMins = mins % 60;
      return `${hrs}:${remMins < 10 ? "0" : ""}${remMins}:${secs < 10 ? "0" : ""}${secs}`;
    }
    return `${mins < 10 ? "0" : ""}${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  if (!recipe) return null;

  const currentStep = steps[activeStepIndex] || {};
  const stepInstruction = currentStep.instruction || "";
  const detectedStepTimers = detectTimers(stepInstruction);
  const isCurrentStepDone = !!checkedSteps[activeStepIndex];

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: "#1c1b18",
          color: "#FAF7F2",
          display: "flex",
          flexDirection: "column"
        }
      }}
    >
      {/* Top Bar */}
      <AppBar sx={{ position: "relative", backgroundColor: "#2b2924", borderBottom: "1px solid #3d3a33" }} elevation={0}>
        <Toolbar sx={{ justifyContent: "space-between", px: { xs: 1, sm: 3 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, overflow: "hidden" }}>
            <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
              <CloseIcon />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" noWrap sx={{ fontFamily: "'Patrick Hand', cursive", fontSize: { xs: '1.2rem', sm: '1.5rem' }, color: '#FFB74D' }}>
                {recipe.recipe_name}
              </Typography>
              <Typography variant="caption" sx={{ color: "#b0aba2", display: "block" }}>
                Cooking Mode
              </Typography>
            </Box>
          </Box>

          {/* Center/Right Controls: Wake Lock & Servings */}
          <Stack direction="row" spacing={1} alignItems="center">
            {wakeLockSupported && (
              <Tooltip title={wakeLockActive ? "Screen will stay awake while cooking" : "Screen wake lock inactive"}>
                <Chip
                  icon={<WbSunnyIcon sx={{ color: wakeLockActive ? '#4caf50 !important' : '#888 !important' }} />}
                  label={isMobile ? (wakeLockActive ? "Awake" : "Dim") : (wakeLockActive ? "Screen Awake" : "Wake Lock Off")}
                  size="small"
                  sx={{
                    backgroundColor: wakeLockActive ? "rgba(76, 175, 80, 0.15)" : "rgba(255, 255, 255, 0.05)",
                    color: wakeLockActive ? "#81c784" : "#888",
                    fontWeight: "bold",
                    border: wakeLockActive ? "1px solid rgba(76, 175, 80, 0.4)" : "1px solid #444"
                  }}
                />
              </Tooltip>
            )}

            {/* In-Cooking Mode Serving Scaler */}
            <Box sx={{ display: "flex", alignItems: "center", bgcolor: "#38352e", borderRadius: 2, px: 0.5, py: 0.2 }}>
              <IconButton
                size="small"
                onClick={() => onServingsChange(Math.max(1, currentServings - 1))}
                sx={{ color: "#FAF7F2", p: 0.5 }}
                disabled={currentServings <= 1}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
              <Typography variant="body2" sx={{ fontWeight: "bold", px: 1, minWidth: 32, textAlign: "center", color: "#FFD54F" }}>
                {currentServings}
              </Typography>
              <IconButton
                size="small"
                onClick={() => onServingsChange(currentServings + 1)}
                sx={{ color: "#FAF7F2", p: 0.5 }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Toggle View Mode */}
            <Tooltip title={viewMode === "step" ? "Switch to Overview (all steps & ingredients)" : "Switch to Step-by-Step Focus"}>
              <IconButton
                color="inherit"
                onClick={() => setViewMode(prev => prev === "step" ? "overview" : "step")}
                sx={{ bgcolor: "#38352e" }}
              >
                {viewMode === "step" ? <ListAltIcon /> : <ViewCarouselIcon />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Toolbar>
        </AppBar>

        {/* Floating Active Timer Banner */}
        {timerSecondsLeft !== null && (
          <Box sx={{
            bgcolor: timerSecondsLeft === 0 ? "#d32f2f" : "#2d3748",
            color: "white",
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid rgba(255,255,255,0.1)",
            animation: timerSecondsLeft === 0 ? "pulse 1s infinite" : "none"
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TimerIcon sx={{ color: timerSecondsLeft === 0 ? "#FFF" : "#FFD54F" }} />
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                {activeTimerLabel}:
              </Typography>
              <Typography variant="h6" sx={{ fontFamily: "monospace", fontWeight: "bold", color: timerSecondsLeft === 0 ? "#FFF" : "#4ade80" }}>
                {formatTimerDisplay(timerSecondsLeft)}
              </Typography>
              {timerSecondsLeft === 0 && (
                <Chip icon={<VolumeUpIcon />} label="Time is up!" color="warning" size="small" sx={{ fontWeight: "bold" }} />
              )}
            </Box>
            <Stack direction="row" spacing={0.5}>
              <IconButton size="small" onClick={toggleTimerPause} sx={{ color: "white" }}>
                {timerIsRunning ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
              </IconButton>
              <IconButton size="small" onClick={resetTimer} sx={{ color: "white" }}>
                <RestartAltIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={cancelTimer} sx={{ color: "white" }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        )}

      {/* Main Content Body */}
      <Box sx={{ flex: 1, overflowY: "auto", p: { xs: 2, sm: 4 }, display: "flex", flexDirection: "column" }}>
        {viewMode === "step" ? (
          /* =================================================== */
          /* STEP-BY-STEP FOCUS MODE                             */
          /* =================================================== */
          <Container maxWidth="md" sx={{ my: "auto", py: 2 }}>
            {/* Step Progress */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle2" sx={{ color: "#FFB74D", fontWeight: "bold", textTransform: "uppercase", letterSpacing: 1 }}>
                  Step {activeStepIndex + 1} of {steps.length}
                </Typography>
                <Typography variant="caption" sx={{ color: "#8e8a82" }}>
                  {Object.values(checkedSteps).filter(Boolean).length} / {steps.length} completed
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={((activeStepIndex + 1) / Math.max(1, steps.length)) * 100}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: "#38352e",
                  "& .MuiLinearProgress-bar": { bgcolor: "#D28415", borderRadius: 4 }
                }}
              />
            </Box>

            {/* Main Step Card */}
            <Paper
              elevation={4}
              sx={{
                p: { xs: 3, sm: 5 },
                borderRadius: 4,
                bgcolor: "#24221d",
                color: "#FAF7F2",
                border: "1px solid #38352e",
                minHeight: { xs: 240, sm: 300 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between"
              }}
            >
              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                  <Chip
                    label={`Step ${activeStepIndex + 1}`}
                    size="medium"
                    sx={{ bgcolor: "#D28415", color: "#FFF", fontWeight: "bold", fontSize: "1rem" }}
                  />
                  <Button
                    variant={isCurrentStepDone ? "contained" : "outlined"}
                    color={isCurrentStepDone ? "success" : "inherit"}
                    startIcon={isCurrentStepDone ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                    onClick={() => onToggleStep(activeStepIndex)}
                    sx={{
                      borderRadius: 3,
                      textTransform: "none",
                      fontWeight: "bold",
                      borderColor: "#4a463c"
                    }}
                  >
                    {isCurrentStepDone ? "Completed" : "Mark as Done"}
                  </Button>
                </Box>

                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: "'Segoe UI', Roboto, sans-serif",
                    lineHeight: 1.6,
                    fontSize: { xs: "1.25rem", sm: "1.6rem" },
                    color: isCurrentStepDone ? "#8e8a82" : "#FAF7F2",
                    textDecoration: isCurrentStepDone ? "line-through" : "none",
                    mt: 3
                  }}
                >
                  {stepInstruction}
                </Typography>
              </Box>

              {/* Detected Timers & Quick Add Chips */}
              <Box sx={{ mt: 4, pt: 2, borderTop: "1px solid #38352e" }}>
                <Typography variant="caption" sx={{ color: "#a5a095", display: "block", mb: 1, fontWeight: "bold" }}>
                  TIMERS FOR THIS STEP
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {detectedStepTimers.length > 0 ? (
                    detectedStepTimers.map((dt, idx) => (
                      <Chip
                        key={idx}
                        icon={<TimerIcon sx={{ color: "#FFD54F !important" }} />}
                        label={`Start ${dt.label}`}
                        onClick={() => startTimer(dt.seconds, dt.label)}
                        sx={{
                          bgcolor: "#332f26",
                          color: "#FFD54F",
                          fontWeight: "bold",
                          fontSize: "0.9rem",
                          py: 2,
                          cursor: "pointer",
                          border: "1px solid #FFB74D",
                          "&:hover": { bgcolor: "#443f33" }
                        }}
                      />
                    ))
                  ) : null}

                  {/* Preset quick timer chips */}
                  <Chip label="+1 min" size="small" onClick={() => startTimer(60, "1 Min Timer")} sx={{ bgcolor: "#2d2a23", color: "#bbb" }} />
                  <Chip label="+5 min" size="small" onClick={() => startTimer(300, "5 Min Timer")} sx={{ bgcolor: "#2d2a23", color: "#bbb" }} />
                  <Chip label="+10 min" size="small" onClick={() => startTimer(600, "10 Min Timer")} sx={{ bgcolor: "#2d2a23", color: "#bbb" }} />
                  <Chip label="+15 min" size="small" onClick={() => startTimer(900, "15 Min Timer")} sx={{ bgcolor: "#2d2a23", color: "#bbb" }} />
                </Stack>
              </Box>
            </Paper>

            {/* Stepper Navigation Buttons (Huge touch targets for messy kitchen hands) */}
            <Grid container spacing={2} sx={{ mt: 3 }}>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  disabled={activeStepIndex === 0}
                  onClick={() => setActiveStepIndex(prev => prev - 1)}
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    py: 2,
                    fontSize: "1.1rem",
                    borderRadius: 3,
                    borderColor: "#4a463c",
                    color: "#FAF7F2",
                    "&:hover": { borderColor: "#FFB74D", bgcolor: "#2b2924" }
                  }}
                >
                  Previous
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={() => {
                    if (activeStepIndex < steps.length - 1) {
                      setActiveStepIndex(prev => prev + 1);
                    } else {
                      setViewMode("overview");
                    }
                  }}
                  endIcon={activeStepIndex === steps.length - 1 ? <CheckCircleIcon /> : <ArrowForwardIcon />}
                  sx={{
                    py: 2,
                    fontSize: "1.1rem",
                    borderRadius: 3,
                    bgcolor: activeStepIndex === steps.length - 1 ? "#388e3c" : "#D28415",
                    "&:hover": { bgcolor: activeStepIndex === steps.length - 1 ? "#2e7d32" : "#b36b10" }
                  }}
                >
                  {activeStepIndex === steps.length - 1 ? "Finish Cooking" : "Next Step"}
                </Button>
              </Grid>
            </Grid>
          </Container>
        ) : (
          /* =================================================== */
          /* FULL OVERVIEW MODE (Ingredients + All Steps)        */
          /* =================================================== */
          <Container maxWidth="lg" sx={{ py: 2 }}>
            <Grid container spacing={4}>
              {/* Ingredients Checklist */}
              <Grid item xs={12} md={5}>
                <Paper sx={{ p: 3, borderRadius: 3, bgcolor: "#24221d", color: "#FAF7F2", border: "1px solid #38352e" }}>
                  <Typography variant="h5" sx={{ fontFamily: "'Patrick Hand', cursive", color: "#FFB74D", mb: 2 }}>
                    Ingredients ({currentServings} servings)
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#a5a095", display: "block", mb: 2 }}>
                    Tap ingredients to check them off as you prep
                  </Typography>
                  <Stack spacing={1}>
                    {ingredients.map((ing, idx) => {
                      const isChecked = !!checkedIngredients[idx];
                      const scaledAmt = scaleIngredientAmount(ing.amount, scaleMultiplier);
                      const unit = ing.measurement_name || ing.measurement_unit || "";
                      const name = ing.ingredient_name || "";
                      return (
                        <Box
                          key={idx}
                          onClick={() => onToggleIngredient(idx)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            p: 1.2,
                            borderRadius: 2,
                            cursor: "pointer",
                            bgcolor: isChecked ? "rgba(255,255,255,0.03)" : "#2c2923",
                            opacity: isChecked ? 0.5 : 1,
                            transition: "all 0.2s ease",
                            "&:hover": { bgcolor: "#38352e" }
                          }}
                        >
                          <Checkbox
                            checked={isChecked}
                            onChange={() => onToggleIngredient(idx)}
                            sx={{ color: "#FFB74D", "&.Mui-checked": { color: "#4caf50" }, p: 0.5, mr: 1 }}
                          />
                          <Typography
                            variant="body1"
                            sx={{
                              fontSize: "1.05rem",
                              textDecoration: isChecked ? "line-through" : "none",
                              color: isChecked ? "#8e8a82" : "#FAF7F2"
                            }}
                          >
                            <strong>{scaledAmt} {unit}</strong> {name}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                </Paper>
              </Grid>

              {/* All Steps Checklist */}
              <Grid item xs={12} md={7}>
                <Paper sx={{ p: 3, borderRadius: 3, bgcolor: "#24221d", color: "#FAF7F2", border: "1px solid #38352e" }}>
                  <Typography variant="h5" sx={{ fontFamily: "'Patrick Hand', cursive", color: "#FFB74D", mb: 2 }}>
                    All Steps
                  </Typography>
                  <Stack spacing={2}>
                    {steps.map((st, idx) => {
                      const isChecked = !!checkedSteps[idx];
                      return (
                        <Box
                          key={idx}
                          onClick={() => onToggleStep(idx)}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            cursor: "pointer",
                            bgcolor: isChecked ? "rgba(255,255,255,0.03)" : "#2c2923",
                            borderLeft: `4px solid ${isChecked ? "#4caf50" : "#D28415"}`,
                            opacity: isChecked ? 0.6 : 1,
                            transition: "all 0.2s ease",
                            "&:hover": { bgcolor: "#38352e" }
                          }}
                        >
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#FFB74D" }}>
                              Step {idx + 1}
                            </Typography>
                            <Checkbox
                              checked={isChecked}
                              onChange={() => onToggleStep(idx)}
                              size="small"
                              sx={{ color: "#FFB74D", "&.Mui-checked": { color: "#4caf50" }, p: 0 }}
                            />
                          </Box>
                          <Typography
                            variant="body1"
                            sx={{
                              lineHeight: 1.6,
                              textDecoration: isChecked ? "line-through" : "none",
                              color: isChecked ? "#8e8a82" : "#FAF7F2"
                            }}
                          >
                            {st.instruction}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        )}
      </Box>
    </Dialog>
  );
};

export default CookingModeDialog;
